import { CandidatePriority, NursingArea } from '../../candidate';
import { EmployerType } from '../../employer';
import { JobTitle } from '../job-title-type';
import { JobTitleEnum } from '../job-titles.enum';
import { DOCIA_JOB_TITLES } from './docia-definition';

export type JobTitleDefinition = Pick<JobTitle, 'area' | 'deputy' | 'order' | 'rank' | 'title' | 'trackingPriority' | 'typeOfEmployer'>;

export const MEDICAL_STAFF_JOB_TITLES: JobTitleDefinition[] = [
  {
    area: [NursingArea.MedicalStaff],
    deputy: false,
    order: 25,
    rank: 7,
    title: JobTitleEnum.QualifiedDentalEmployee,
    trackingPriority: CandidatePriority.B4,
    typeOfEmployer: [EmployerType.DentalPractice],
  },
  {
    area: [NursingArea.MedicalStaff],
    deputy: false,
    order: 26,
    rank: 3,
    title: JobTitleEnum.PracticeManager,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.DoctorOffice, EmployerType.DentalPractice, EmployerType.Physiotherapy],
  },
  {
    area: [NursingArea.MedicalStaff],
    deputy: false,
    order: 37,
    rank: 7,
    title: JobTitleEnum.LaboratoryAssistant,
    trackingPriority: CandidatePriority.B3,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DoctorOffice, EmployerType.DentalPractice],
  },
  {
    area: [NursingArea.MedicalStaff],
    deputy: false,
    order: 38,
    rank: 7,
    title: JobTitleEnum.FunctionalDiagnosticsAssistant,
    trackingPriority: CandidatePriority.B3,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.MedicalStaff],
    deputy: false,
    order: 39,
    rank: 7,
    title: JobTitleEnum.IntensiveCareTechnicalAssistance,
    trackingPriority: CandidatePriority.B6,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.MedicalStaff],
    deputy: false,
    order: 80,
    rank: 8,
    title: JobTitleEnum.AdministrativeStaff,
    trackingPriority: CandidatePriority.D2,
    typeOfEmployer: [EmployerType.DoctorOffice, EmployerType.DentalPractice, EmployerType.Hospital, EmployerType.Physiotherapy],
  },
  {
    area: [NursingArea.MedicalStaff],
    deputy: false,
    order: 81,
    rank: 8,
    title: JobTitleEnum.MedicalAssistance,
    trackingPriority: CandidatePriority.D2,
    typeOfEmployer: [EmployerType.DoctorOffice, EmployerType.DentalPractice, EmployerType.Hospital],
  },
];

export const THERAPIST_JOB_TITLES_ENUMS = [
  JobTitleEnum.OccupationalTherapist,
  JobTitleEnum.Osteopath,
  JobTitleEnum.PhysicalTherapist,
  JobTitleEnum.SpeechTherapist,
] as const;

export type TherapistJobTitle = (typeof THERAPIST_JOB_TITLES_ENUMS)[number];

const ORDER_BY_THERAPIST_JOB_TITLE: Record<TherapistJobTitle, number> = {
  [JobTitleEnum.OccupationalTherapist]: 51,
  [JobTitleEnum.Osteopath]: 53,
  [JobTitleEnum.PhysicalTherapist]: 50,
  [JobTitleEnum.SpeechTherapist]: 52,
};

const PRIORITY_BY_THERAPIST_JOB_TITLE: Record<TherapistJobTitle, CandidatePriority> = {
  [JobTitleEnum.PhysicalTherapist]: CandidatePriority.B5,
  [JobTitleEnum.OccupationalTherapist]: CandidatePriority.B6,
  [JobTitleEnum.Osteopath]: CandidatePriority.B6,
  [JobTitleEnum.SpeechTherapist]: CandidatePriority.B6,
};

export const THERAPIST_JOB_TITLES: JobTitleDefinition[] = THERAPIST_JOB_TITLES_ENUMS.map(title => ({
  area: [NursingArea.ElderlyCare, NursingArea.Nursing, NursingArea.MedicalStaff, NursingArea.Other],
  deputy: false,
  order: ORDER_BY_THERAPIST_JOB_TITLE[title],
  rank: 7,
  title,
  trackingPriority: PRIORITY_BY_THERAPIST_JOB_TITLE[title],
  typeOfEmployer: Object.values(EmployerType).filter(type =>
    title === JobTitleEnum.Osteopath
      ? type !== EmployerType.DentalPractice && type !== EmployerType.Physiotherapy
      : type !== EmployerType.DentalPractice,
  ),
}));

export const HEALTH_CARE_JOB_TITLES: JobTitleDefinition[] = [
  {
    area: [NursingArea.ElderlyCare],
    deputy: false,
    order: 1,
    rank: 8,
    title: JobTitleEnum.Companion,
    trackingPriority: CandidatePriority.C,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant],
  },
  {
    area: [NursingArea.ElderlyCare, NursingArea.Nursing],
    deputy: false,
    order: 2,
    rank: 8,
    title: JobTitleEnum.AssistantElderlyCareNurse,
    trackingPriority: CandidatePriority.D2,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 3,
    rank: 8,
    title: JobTitleEnum.AssistantNurse,
    trackingPriority: CandidatePriority.D2,
    typeOfEmployer: [EmployerType.Ambulant, EmployerType.Hospital, EmployerType.NursingHome],
  },
  {
    area: [NursingArea.ElderlyCare, NursingArea.Nursing],
    deputy: false,
    order: 4,
    rank: 6,
    title: JobTitleEnum.ElderlyCareNurse,
    trackingPriority: CandidatePriority.B,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 5,
    rank: 6,
    title: JobTitleEnum.Nurse,
    trackingPriority: CandidatePriority.B,
    typeOfEmployer: [EmployerType.Ambulant, EmployerType.Hospital, EmployerType.Intensive, EmployerType.NursingHome],
  },
  {
    area: [NursingArea.Nursing],
    deputy: false,
    order: 6,
    rank: 6,
    title: JobTitleEnum.PediatricNurse,
    trackingPriority: CandidatePriority.B,
    typeOfEmployer: [EmployerType.Ambulant, EmployerType.Hospital, EmployerType.Intensive],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 7,
    rank: 3,
    title: JobTitleEnum.Instructor,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: true,
    order: 8,
    rank: 4,
    title: JobTitleEnum.Instructor,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing],
    deputy: false,
    order: 9,
    rank: 2,
    title: JobTitleEnum.UnitManager,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing],
    deputy: true,
    order: 10,
    rank: 3,
    title: JobTitleEnum.UnitManager,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.ElderlyCare],
    deputy: false,
    order: 11,
    rank: 2,
    title: JobTitleEnum.ResidentialSectorManager,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome],
  },
  {
    area: [NursingArea.ElderlyCare],
    deputy: true,
    order: 12,
    rank: 3,
    title: JobTitleEnum.ResidentialSectorManager,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 13,
    rank: 1,
    title: JobTitleEnum.NursingSupervisor,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: true,
    order: 14,
    rank: 3,
    title: JobTitleEnum.NursingSupervisor,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing],
    deputy: false,
    order: 15,
    rank: 1,
    title: JobTitleEnum.NursingDirector,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing],
    deputy: true,
    order: 16,
    rank: 1,
    title: JobTitleEnum.NursingDirector,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.ElderlyCare],
    deputy: false,
    order: 17,
    rank: 1,
    title: JobTitleEnum.HomeManager,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome],
  },
  {
    area: [NursingArea.ElderlyCare],
    deputy: true,
    order: 18,
    rank: 1,
    title: JobTitleEnum.HomeManager,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 19,
    rank: 2,
    title: JobTitleEnum.QualityManagement,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 20,
    rank: 2,
    title: JobTitleEnum.HygieneSpecialist,
    trackingPriority: CandidatePriority.B,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 21,
    rank: 4,
    title: JobTitleEnum.TeamLeader,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: true,
    order: 22,
    rank: 3,
    title: JobTitleEnum.TeamLeader,
    trackingPriority: CandidatePriority.A,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing],
    deputy: false,
    order: 23,
    rank: 6,
    title: JobTitleEnum.Midwife,
    trackingPriority: CandidatePriority.B,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 24,
    rank: 7,
    title: JobTitleEnum.MedicalAssistant,
    trackingPriority: CandidatePriority.B3,
    typeOfEmployer: [EmployerType.DoctorOffice, EmployerType.Hospital, EmployerType.Physiotherapy],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 25,
    rank: 8,
    title: JobTitleEnum.AssistantRemedialCareNurse,
    trackingPriority: CandidatePriority.D2,
    typeOfEmployer: [EmployerType.NursingHome],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 26,
    rank: 6,
    title: JobTitleEnum.RemedialCareNurse,
    trackingPriority: CandidatePriority.C,
    typeOfEmployer: [EmployerType.NursingHome],
  },
  {
    area: [NursingArea.Nursing],
    deputy: false,
    order: 27,
    rank: 8,
    title: JobTitleEnum.Paramedic,
    trackingPriority: CandidatePriority.C,
    typeOfEmployer: [EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare, NursingArea.MedicalStaff],
    deputy: false,
    order: 95,
    rank: 9,
    title: JobTitleEnum.CareerChangers,
    trackingPriority: CandidatePriority.F,
    typeOfEmployer: [
      EmployerType.Ambulant,
      EmployerType.DentalPractice,
      EmployerType.DoctorOffice,
      EmployerType.Hospital,
      EmployerType.Intensive,
      EmployerType.NursingHome,
      EmployerType.Physiotherapy,
    ],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 30,
    rank: 7,
    title: JobTitleEnum.AnestheticAssistant,
    trackingPriority: CandidatePriority.B2,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DentalPractice],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 31,
    rank: 6,
    title: JobTitleEnum.OperationAssistant,
    trackingPriority: CandidatePriority.B2,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DentalPractice, EmployerType.DoctorOffice],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 32,
    rank: 7,
    title: JobTitleEnum.TechnicalAssistant,
    trackingPriority: CandidatePriority.B3,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DentalPractice, EmployerType.DoctorOffice],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 33,
    rank: 7,
    title: JobTitleEnum.PharmaceuticalAssistant,
    trackingPriority: CandidatePriority.B6,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DoctorOffice],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 34,
    rank: 7,
    title: JobTitleEnum.RadiologyAssistant,
    trackingPriority: CandidatePriority.B3,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DentalPractice, EmployerType.DoctorOffice],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 35,
    rank: 7,
    title: JobTitleEnum.SurgeryAssistant,
    trackingPriority: CandidatePriority.B2,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DoctorOffice],
  },
  {
    area: [NursingArea.Nursing, NursingArea.MedicalStaff],
    deputy: false,
    order: 35,
    rank: 7,
    title: JobTitleEnum.ChemistAssistant,
    trackingPriority: CandidatePriority.B6,
    typeOfEmployer: [EmployerType.Hospital, EmployerType.DoctorOffice],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare, NursingArea.MedicalStaff],
    deputy: false,
    order: 85,
    rank: 8,
    title: JobTitleEnum.Apprenticeship,
    trackingPriority: CandidatePriority.E,
    typeOfEmployer: [
      EmployerType.NursingHome,
      EmployerType.Ambulant,
      EmployerType.Intensive,
      EmployerType.Hospital,
      EmployerType.Physiotherapy,
    ],
  },
  {
    area: [NursingArea.Nursing, NursingArea.ElderlyCare],
    deputy: false,
    order: 100,
    rank: 9,
    title: JobTitleEnum.Others,
    trackingPriority: CandidatePriority.F,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.ElderlyCare],
    deputy: false,
    order: 1,
    rank: 6,
    title: JobTitleEnum.NursingSpecialistGeriatric,
    trackingPriority: CandidatePriority.B,
    typeOfEmployer: [EmployerType.NursingHome, EmployerType.Ambulant, EmployerType.Intensive, EmployerType.Hospital],
  },
  {
    area: [NursingArea.Nursing],
    deputy: false,
    order: 1,
    rank: 6,
    title: JobTitleEnum.NursingSpecialistHealthCare,
    trackingPriority: CandidatePriority.B,
    typeOfEmployer: [EmployerType.Ambulant, EmployerType.Hospital, EmployerType.Intensive, EmployerType.NursingHome],
  },
];

export const JOB_TITLES_DEFINITIONS: JobTitleDefinition[] = [
  ...HEALTH_CARE_JOB_TITLES,
  ...THERAPIST_JOB_TITLES,
  ...MEDICAL_STAFF_JOB_TITLES,
  ...DOCIA_JOB_TITLES,
] as const;
